import { User } from '@/types/models/user';

type MoneyLike = {
  amount: number | string;
  currency_code?: string;
  currencyCode?: string;
};

type BillingInterval = 'day' | 'week' | 'month' | 'year';

const INTRO_PRICE_TYPES = new Set(['intro', 'paidTrial']);
const BILLING_INTERVALS = new Set(['day', 'week', 'month', 'year']);

const normalizeAmount = (amount?: number | string | null) => {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    return 0;
  }

  return numericAmount / 100;
};

const getSingleSeatPrices = (prices: PaddlePrice[]) =>
  prices.filter((price) => price.quantity?.maximum === 1);

const getCurrencyCode = (money?: MoneyLike | null) =>
  money?.currency_code ?? money?.currencyCode ?? 'USD';

const normalizeBillingInterval = (
  interval?: string,
  fallback: BillingInterval = 'month'
): BillingInterval =>
  BILLING_INTERVALS.has(interval ?? '')
    ? (interval as BillingInterval)
    : fallback;

export const isIntroPriceType = (priceType?: unknown) =>
  INTRO_PRICE_TYPES.has(String(priceType ?? ''));

export const isIntroPrice = (price?: PaddlePrice | null) =>
  isIntroPriceType(price?.custom_data?.priceType);

const isRegularPrice = (price?: PaddlePrice | null) => !isIntroPrice(price);

const getFirstMatchingPrice = (
  prices: PaddlePrice[],
  predicate: (price: PaddlePrice) => boolean
) => getSingleSeatPrices(prices).find(predicate);

const getFirstRegularPrice = (prices: PaddlePrice[]) =>
  getFirstMatchingPrice(prices, isRegularPrice);

const getFirstIntroPrice = (prices: PaddlePrice[]) =>
  getFirstMatchingPrice(prices, isIntroPrice);

export const isIntroEligible = (user?: Pick<User, 'isIntroItem'> | null) =>
  !INTRO_PRICE_TYPES.has(user?.isIntroItem ?? '');

export const getPrimaryPaywallPrice = ({
  monthlyPrices,
  weeklyPrices,
  user,
}: {
  monthlyPrices: PaddlePrice[];
  weeklyPrices: PaddlePrice[];
  user?: Pick<User, 'isIntroItem'> | null;
}) => {
  if (isIntroEligible(user)) {
    return (
      getFirstIntroPrice(weeklyPrices) ??
      getFirstIntroPrice(monthlyPrices) ??
      getFirstRegularPrice(monthlyPrices) ??
      getFirstRegularPrice(weeklyPrices) ??
      getSingleSeatPrices(monthlyPrices)[0] ??
      getSingleSeatPrices(weeklyPrices)[0]
    );
  }

  return (
    getFirstRegularPrice(monthlyPrices) ??
    getFirstRegularPrice(weeklyPrices) ??
    getSingleSeatPrices(monthlyPrices)[0] ??
    getSingleSeatPrices(weeklyPrices)[0]
  );
};

export const getAnnualPaywallPrice = (yearlyPrices: PaddlePrice[]) =>
  getFirstRegularPrice(yearlyPrices) ?? getSingleSeatPrices(yearlyPrices)[0];

export const getSelectedPaywallPrice = ({
  selectedPlan,
  monthlyPrices,
  weeklyPrices,
  yearlyPrices,
  user,
}: {
  selectedPlan?: 'weekly' | 'annual';
  monthlyPrices: PaddlePrice[];
  weeklyPrices: PaddlePrice[];
  yearlyPrices: PaddlePrice[];
  user?: Pick<User, 'isIntroItem'> | null;
}) =>
  selectedPlan === 'annual'
    ? getAnnualPaywallPrice(yearlyPrices)
    : getPrimaryPaywallPrice({ monthlyPrices, weeklyPrices, user });

export const getIntroSubscriptionPrice = ({
  monthlyPrices,
  weeklyPrices,
}: {
  monthlyPrices: PaddlePrice[];
  weeklyPrices: PaddlePrice[];
}) =>
  getFirstIntroPrice(weeklyPrices) ??
  getFirstIntroPrice(monthlyPrices) ??
  undefined;

export const getRecurringPriceForSelectedPrice = ({
  selectedPrice,
  monthlyPrices,
  weeklyPrices,
  yearlyPrices,
}: {
  selectedPrice?: PaddlePrice | null;
  monthlyPrices: PaddlePrice[];
  weeklyPrices: PaddlePrice[];
  yearlyPrices: PaddlePrice[];
}) => {
  if (!selectedPrice) {
    return undefined;
  }

  if (!isIntroPrice(selectedPrice)) {
    return selectedPrice;
  }

  const targetPriceId =
    typeof selectedPrice.custom_data?.productType === 'string'
      ? selectedPrice.custom_data.productType
      : undefined;
  const allPrices = [...monthlyPrices, ...weeklyPrices, ...yearlyPrices];
  const targetPrice = targetPriceId
    ? allPrices.find((price) => price.id === targetPriceId)
    : undefined;

  if (targetPrice) {
    return targetPrice;
  }

  const selectedInterval = selectedPrice.billing_cycle?.interval;
  const matchingIntervalPrices =
    selectedInterval === 'year'
      ? yearlyPrices
      : selectedInterval === 'week'
        ? weeklyPrices
        : monthlyPrices;

  return (
    getFirstRegularPrice(monthlyPrices) ??
    getFirstRegularPrice(matchingIntervalPrices) ??
    getFirstRegularPrice(weeklyPrices) ??
    getFirstRegularPrice(yearlyPrices) ??
    monthlyPrices.find(isRegularPrice) ??
    matchingIntervalPrices.find(isRegularPrice) ??
    weeklyPrices.find(isRegularPrice) ??
    yearlyPrices.find(isRegularPrice) ??
    selectedPrice
  );
};

export const getDisplayPlanName = (
  price?: PaddlePrice | null,
  fallback = 'PDF Genius Pro'
) => {
  const introTitle =
    typeof price?.custom_data?.title === 'string'
      ? price.custom_data.title.trim()
      : '';

  return introTitle || price?.name || fallback;
};

export const hasTrialPeriod = (price?: PaddlePrice | null) =>
  Boolean(price?.trial_period?.frequency) ||
  price?.trial_period_unit_price?.amount != null;

export const shouldUseTrialPricing = ({
  price,
  user,
  forceTrial = false,
}: {
  price?: PaddlePrice | null;
  user?: Pick<User, 'isIntroItem'> | null;
  forceTrial?: boolean;
}) => {
  if (!hasTrialPeriod(price) && !isIntroPrice(price)) {
    return false;
  }

  return forceTrial || isIntroPrice(price) || isIntroEligible(user);
};

const TRIAL_INTERVAL_DAYS: Record<string, number> = {
  day: 1,
  week: 7,
  month: 30,
  year: 365,
};

export const getTrialDays = (price?: PaddlePrice | null) => {
  const frequency = price?.trial_period?.frequency;
  const interval = price?.trial_period?.interval;

  if (frequency && interval) {
    return frequency * (TRIAL_INTERVAL_DAYS[interval] ?? 1);
  }

  if (isIntroPrice(price) && price?.billing_cycle) {
    return (
      price.billing_cycle.frequency *
      (TRIAL_INTERVAL_DAYS[price.billing_cycle.interval] ?? 1)
    );
  }

  return 0;
};

export const getTrialDurationText = (price?: PaddlePrice | null) => {
  const trialDays = getTrialDays(price);

  if (!trialDays) {
    return '';
  }

  return `${trialDays}-day`;
};

export const getImmediateChargeAmount = (price?: PaddlePrice | null) => {
  if (price?.trial_period_unit_price?.amount != null) {
    return normalizeAmount(price.trial_period_unit_price.amount);
  }

  return normalizeAmount(price?.unit_price?.amount);
};

export const getImmediateChargeCurrency = (price?: PaddlePrice | null) =>
  getCurrencyCode(price?.trial_period_unit_price ?? price?.unit_price);

export const getRecurringChargeAmount = (price?: PaddlePrice | null) =>
  normalizeAmount(price?.unit_price?.amount);

export const getRecurringChargeCurrency = (price?: PaddlePrice | null) =>
  getCurrencyCode(price?.unit_price);

export const getPriceInterval = (
  price?: PaddlePrice | Price | null,
  fallback: 'day' | 'week' | 'month' | 'year' = 'month'
) => {
  if (price == null) {
    return fallback;
  }

  if ('billing_cycle' in price) {
    return price.billing_cycle?.interval ?? fallback;
  }

  return (price as Price).billing ?? fallback;
};

export const getPricePeriodLabel = (
  price?: PaddlePrice | Price | null,
  fallback: 'day' | 'week' | 'month' | 'year' = 'month'
) => `/${getPriceInterval(price, fallback)}`;

export const getTrialPricePeriodLabel = (
  price?: PaddlePrice | null,
  fallback: BillingInterval = 'week'
) => `/${getIntroPeriod(price, fallback).interval}`;

export const getIntroPeriod = (
  price?: PaddlePrice | null,
  fallback: BillingInterval = 'week'
) => {
  const frequency =
    price?.trial_period?.frequency ?? price?.billing_cycle?.frequency ?? 1;
  const interval = normalizeBillingInterval(
    price?.trial_period?.interval ?? price?.billing_cycle?.interval,
    fallback
  );

  return { frequency, interval };
};

const RECURRING_PERIOD_TEXT: Record<string, string> = {
  day: 'day',
  week: '7 days',
  month: '30 days',
  year: '12 months',
};

export const getRecurringPeriodText = (price?: PaddlePrice | Price | null) => {
  const interval = getPriceInterval(price);
  const frequency =
    price && 'billing_cycle' in price
      ? (price.billing_cycle?.frequency ?? 1)
      : 1;

  if (frequency > 1) {
    return `${frequency} ${interval}s`;
  }

  return RECURRING_PERIOD_TEXT[interval] ?? interval;
};

export const getIntervalAdjective = (
  interval?: 'day' | 'week' | 'month' | 'year'
) => {
  switch (interval) {
    case 'day':
      return 'daily';
    case 'week':
      return 'weekly';
    case 'year':
      return 'yearly';
    default:
      return 'monthly';
  }
};
